import Linkify from '../components/Linkify';
import React, { useState, useEffect, useContext, useRef } from 'react';
import TaskChecklist from '../components/TaskChecklist';
import RescheduleCalendar from '../components/RescheduleCalendar';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatDate, isOverdue, isToday } from '../utils/helpers';

const TYPE_LABEL = {
  auto: { label: 'Авто', color: '#7c6cf0' },
  manual: { label: 'Ручная', color: '#aaa' },
  chain: { label: 'Цепочка', color: '#f5a623' },
  chain_first: { label: 'Цепочка', color: '#f5a623' },
  overdue_1: { label: 'Долг · шаг 1', color: '#e53e3e' },
  overdue_2: { label: 'Долг · претензия', color: '#e53e3e' },
  overdue_3: { label: 'Долг · приказ', color: '#e53e3e' },
};

const SOURCE_META = {
  client:  { label: 'Клиентская', color: '#7c6cf0', bg: '#7c6cf022' },
  personal: { label: 'Личная',    color: '#10b981', bg: '#10b98122' },
  roadmap:  { label: 'Развитие',  color: '#f5a623', bg: '#f5a62322' },
  case:     { label: 'Дело',      color: '#0ea5e9', bg: '#0ea5e922' },
};

function groupTasks(tasks) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
  const groups = [
    { key: 'overdue',  label: 'Просрочено',  color: 'var(--red)',       tasks: [] },
    { key: 'today',    label: 'Сегодня',     color: 'var(--accent)',    tasks: [] },
    { key: 'tomorrow', label: 'Завтра',      color: '#6366f1',          tasks: [] },
    { key: 'week',     label: 'Эта неделя', color: 'var(--yellow)',    tasks: [] },
    { key: 'later',    label: 'Позже',       color: 'var(--text-muted)',tasks: [] },
    { key: 'nodate',   label: 'Без даты',    color: 'var(--text-muted)',tasks: [] },
  ];
  tasks.filter(t => !t.done).forEach(t => {
    if (!t.task_date) { groups[5].tasks.push(t); return; }
    const d = new Date(t.task_date);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day < today) groups[0].tasks.push(t);
    else if (day.getTime() === today.getTime()) groups[1].tasks.push(t);
    else if (day.getTime() === tomorrow.getTime()) groups[2].tasks.push(t);
    else if (day <= weekEnd) groups[3].tasks.push(t);
    else groups[4].tasks.push(t);
  });
  return groups.filter(g => g.tasks.length > 0);
}

// Ручной порядок (перетаскивание): задачи с sort_order — сверху по нему, остальные ниже в прежнем порядке
function byManualOrder(list) {
  return [...list].sort((a, b) => {
    const x = a.sort_order ?? null, y = b.sort_order ?? null;
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return x - y;
  });
}

function ClientDrawer({ clientId, clientName, onClose, onTasksChanged }) {
  const navigate = useNavigate();
  const [clientTasks, setClientTasks] = useState([]);
  const [newText, setNewText] = useState('');
  const [newDate, setNewDate] = useState('');
  const [adding, setAdding] = useState(false);

  const loadClientTasks = () => api.get(`/tasks/client/${clientId}`).then(r => setClientTasks(r.data)).catch(()=>{});
  useEffect(() => { if (clientId) loadClientTasks(); }, [clientId]);

  const toggle = async (id) => { await api.patch(`/tasks/${id}/toggle`); loadClientTasks(); onTasksChanged(); };
  const addTask = async () => {
    if (!newText.trim()) return;
    await api.post('/tasks', { client_id: clientId, text: newText.trim(), task_date: newDate || null });
    setNewText(''); setNewDate(''); setAdding(false);
    loadClientTasks(); onTasksChanged();
  };

  const active = clientTasks.filter(t => !t.done);
  const done = clientTasks.filter(t => t.done);

  return (
    <>
      <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',zIndex:200}} />
      <div style={{position:'fixed',top:0,right:0,bottom:0,width:420,background:'var(--bg-surface)',boxShadow:'-4px 0 24px rgba(0,0,0,0.18)',zIndex:201,display:'flex',flexDirection:'column'}}>
        <div style={{padding:'18px 20px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:2}}>Клиент</div>
            <div style={{fontWeight:700,fontSize:16,color:'var(--accent)',cursor:'pointer'}} onClick={()=>navigate(`/clients/${clientId}`)}>{clientName} ↗</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'var(--text-muted)'}}>×</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'12px 0'}}>
          {active.map(t => (
            <div key={t.id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 20px',borderBottom:'1px solid var(--border)'}}>
              <input type="checkbox" checked={false} onChange={()=>toggle(t.id)} style={{width:16,height:16,cursor:'pointer',marginTop:2}} />
              <div style={{flex:1}}>
                <div style={{fontSize:13,marginBottom:3}}><Linkify text={t.text}/></div>
                {t.task_date && <span style={{fontSize:11,color:isOverdue(t.task_date)?'var(--red)':isToday(t.task_date)?'var(--accent)':'var(--text-muted)'}}>{formatDate(t.task_date)}</span>}
              </div>
            </div>
          ))}
          {done.map(t => (
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 20px',borderBottom:'1px solid var(--border)',opacity:0.5}}>
              <input type="checkbox" checked={true} onChange={()=>toggle(t.id)} style={{width:16,height:16,cursor:'pointer'}} />
              <span style={{fontSize:13,textDecoration:'line-through'}}>{t.text}</span>
            </div>
          ))}
          {!active.length && !done.length && <div style={{textAlign:'center',color:'var(--text-muted)',padding:40}}>Нет задач</div>}
        </div>
        <div style={{padding:16,borderTop:'1px solid var(--border)'}}>
          {!adding ? (
            <button onClick={()=>setAdding(true)} style={{width:'100%',padding:10,background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:'pointer'}}>+ Добавить задачу</button>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <input autoFocus placeholder="Текст задачи..." value={newText} onChange={e=>setNewText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addTask()}
                style={{padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-input)',color:'var(--text)',fontSize:14}} />
              <input type="date" value={newDate} onChange={e=>setNewDate(e.target.value)}
                style={{padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-input)',color:'var(--text)',fontSize:14}} />
              <div style={{display:'flex',gap:8}}>
                <button onClick={addTask} style={{flex:1,padding:8,background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontWeight:600,cursor:'pointer'}}>Добавить</button>
                <button onClick={()=>{setAdding(false);setNewText('');setNewDate('');}} style={{padding:'8px 16px',background:'var(--bg-hover)',border:'none',borderRadius:6,cursor:'pointer',color:'var(--text)'}}>Отмена</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function TaskCard({ t, onToggle, onOpenDrawer, onReschedule, onEdit, onChecklist, group, onDragStart, onCardPointerDown, dragging }) {
  const src = SOURCE_META[t.source] || SOURCE_META.client;
  const type = TYPE_LABEL[t.task_type] || TYPE_LABEL.manual;
  const isHigh = t.priority === 'high';
  const [showPicker, setShowPicker] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(t.text);
  const [showComments, setShowComments] = React.useState(false);
  const canEdit = onEdit && t.source !== 'roadmap';
  const saveEdit = async (e) => { e.stopPropagation(); const v = draft.trim(); if (!v) return; await onEdit(t, v); setEditing(false); };

  const handleToggle = (e) => { e.stopPropagation(); onToggle(t); };
  const handleClick = () => {
    if (String(window.getSelection?.() || '')) return;   // выделили текст (номер дела и т.п.) — не открываем карточку
    if (t.source === 'client') onOpenDrawer(t.client_id, t.client_name);
    if (t.source === 'case') window.location.href = '/cases/' + t.case_id;
  };

  return (
    <div data-task-key={t.source+t.id} data-group={group}
      style={{display:'flex',alignItems:'flex-start',gap:12,padding:'12px 16px',borderBottom:'1px solid var(--border)',background:dragging?'var(--bg-hover)':isHigh?'rgba(229,57,53,0.04)':'transparent',boxShadow:dragging?'0 2px 12px rgba(0,0,0,0.15)':'none',position:'relative',zIndex:dragging?2:'auto',WebkitTouchCallout:'none',cursor:dragging?'grabbing':(t.source==='client'||t.source==='case')?'pointer':'default'}}
      onClick={handleClick}
      onPointerDown={onCardPointerDown ? e => onCardPointerDown(e, t.source+t.id, group) : undefined}
      onContextMenu={onCardPointerDown ? e => { if (e.nativeEvent.pointerType !== 'mouse' && !e.target.closest('[data-nodrag]')) e.preventDefault(); } : undefined}>
      {onDragStart && (
        <span title="Перетащите, чтобы поменять порядок (можно тянуть и за саму задачу)" onClick={e=>e.stopPropagation()}
          onPointerDown={e=>{ e.preventDefault(); e.stopPropagation(); onDragStart(t.source+t.id, group); }}
          style={{cursor:'grab',touchAction:'none',userSelect:'none',color:'var(--text-muted)',fontSize:22,lineHeight:1,padding:'4px 8px 8px',margin:'-6px -8px 0 -12px',flexShrink:0,alignSelf:'stretch',display:'flex',alignItems:'center'}}>⋮⋮</span>
      )}
      <input type="checkbox" checked={!!t.done} onChange={handleToggle} onClick={e=>e.stopPropagation()}
        style={{width:18,height:18,cursor:'pointer',marginTop:2,flexShrink:0}} />
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3,flexWrap:'wrap'}}>
          {t.source === 'client' && (
            <span style={{fontWeight:700,fontSize:14,color:'var(--accent)',cursor:'pointer'}}
              onClick={e=>{e.stopPropagation();window.location.href='/clients/'+t.client_id;}}>{t.client_name} ↗</span>
          )}
          {t.source === 'case' && (
            <span style={{fontWeight:700,fontSize:14,color:'#0ea5e9',cursor:'pointer'}}>{t.client_name} ↗</span>
          )}
          <span style={{fontSize:11,padding:'1px 7px',borderRadius:10,background:src.bg,color:src.color,fontWeight:600}}>{src.label}</span>
          {t.source === 'client' && <span style={{fontSize:11,padding:'1px 7px',borderRadius:10,background:type.color+'22',color:type.color,fontWeight:600}}>{type.label}</span>}
          {isHigh && <span style={{fontSize:11,color:'var(--red)',fontWeight:700}}>● Срочно</span>}
        </div>
        {editing ? (
          <div onClick={e=>e.stopPropagation()} style={{marginBottom:6}}>
            <textarea value={draft} onChange={e=>setDraft(e.target.value)} autoFocus rows={Math.min(8, Math.max(2, Math.ceil(draft.length/70)))}
              onKeyDown={e=>{ if (e.key==='Escape') { setEditing(false); setDraft(t.text); } if (e.key==='Enter' && (e.ctrlKey||e.metaKey)) saveEdit(e); }}
              style={{width:'100%',fontSize:14,padding:'6px 8px',borderRadius:6,border:'1px solid var(--accent)',background:'var(--bg-input)',color:'var(--text)',fontFamily:'inherit',resize:'vertical'}} />
            <div style={{display:'flex',gap:6,marginTop:4}}>
              <button onClick={saveEdit} style={{fontSize:12,padding:'4px 12px',borderRadius:6,border:'none',background:'var(--accent)',color:'#fff',cursor:'pointer'}}>Сохранить</button>
              <button onClick={e=>{e.stopPropagation();setEditing(false);setDraft(t.text);}} style={{fontSize:12,padding:'4px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-hover)',color:'var(--text)',cursor:'pointer'}}>Отмена</button>
              <span style={{fontSize:11,color:'var(--text-muted)',alignSelf:'center'}}>Ctrl+Enter — сохранить, Esc — отмена</span>
            </div>
          </div>
        ) : (
          <div data-nodrag style={{fontSize:14,marginBottom:6,whiteSpace:'pre-wrap',overflowWrap:'anywhere',cursor:'text',userSelect:'text',WebkitUserSelect:'text',WebkitTouchCallout:'default'}}><Linkify text={t.text}/></div>
        )}
        {t.comments && t.comments.length > 0 && (
          <div style={{marginBottom:6}} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setShowComments(v=>!v)}
              style={{fontSize:12,padding:'2px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-hover)',cursor:'pointer',color:'var(--text)'}}>
              💬 {showComments ? 'Скрыть' : 'Комментарий'}{t.comments.length > 1 ? ` (${t.comments.length})` : ''}
            </button>
            {showComments && t.comments.map(c => (
              <div key={c.id} data-nodrag style={{cursor:'text',userSelect:'text',WebkitUserSelect:'text',WebkitTouchCallout:'default',fontSize:13,marginTop:6,padding:'8px 10px',borderRadius:6,background:'var(--bg-hover)',whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>
                <Linkify text={c.text}/>
              </div>
            ))}
          </div>
        )}
        {t.source !== 'roadmap' && <TaskChecklist key={t.source+t.id+(t.checklist||[]).length} task={t} onChange={d => { if (!!d.done !== !!t.done && onChecklist) onChecklist(); }} />}
        <div style={{display:'flex',gap:4,flexWrap:'wrap',alignItems:'center'}}>
          {t.task_date && (
            <span style={{fontSize:11,marginRight:4,color:isOverdue(t.task_date)?'var(--red)':isToday(t.task_date)?'var(--accent)':'var(--text-muted)',fontWeight:isOverdue(t.task_date)||isToday(t.task_date)?700:400}}>
              {formatDate(t.task_date)}
            </span>
          )}
          {canEdit && !editing && (
            <button onClick={e=>{e.stopPropagation();setDraft(t.text);setEditing(true);}} title="Изменить текст задачи"
              style={{fontSize:12,padding:'2px 6px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-hover)',cursor:'pointer',lineHeight:1.2,color:'var(--text)'}}>изменить</button>
          )}
          {onReschedule && !t.done && (
            <div style={{position:'relative',display:'inline-flex',alignItems:'center'}} onClick={e=>e.stopPropagation()}>
              <button
                onClick={e=>{e.stopPropagation();setShowPicker(v=>!v);}}
                title="Перенести задачу"
                style={{fontSize:14,padding:'2px 6px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-hover)',cursor:'pointer',lineHeight:1}}
              >дата</button>
              {showPicker && (
                <RescheduleCalendar value={t.task_date} onClose={()=>setShowPicker(false)}
                  onPick={d=>{ onReschedule(t.id, d, 0, t.source, true); setShowPicker(false); }} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const [allTasks, setAllTasks] = useState({ client: [], personal: [], roadmap: [], case: [] });
  const [sourceTab, setSourceTab] = useState('all');
  const [filter, setFilter] = useState('today');      // при открытии — только задачи на сегодня
  const [drawer, setDrawer] = useState(null);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState({});      // «Позже» и «Без даты» свёрнуты, пока не раскроют нажатием на заголовок
  const FOLDED = ['later', 'nodate'];
  const [drag, setDrag] = useState(null);                // {group, keys, dragKey} — пока тянем задачу
  const dragRef = useRef(null);
  dragRef.current = drag;

  const load = () => Promise.all([api.get('/tasks/all'), api.get('/cases/tasks/all').catch(() => ({ data: [] }))])
    .then(([r, rc]) => setAllTasks({ ...r.data, case: rc.data })).catch(()=>{});
  useEffect(() => { load(); }, []);

  const toggleTask = async (t) => {
    if (t.source === 'roadmap') {
      await api.patch(`/roadmap/tasks/${t.id}/status`, { status: 'done' });
    } else {
      await api.patch(`/tasks/${t.id}/toggle`);
    }
    load();
  };

  const editTask = async (t, text) => {
    try { await api.patch(`/tasks/${t.id}/text`, { text }); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Не удалось сохранить'); }
  };
  const rescheduleTask = async (id, currentDate, days, source, absolute = false) => {
    let newDate;
    if (absolute) {
      newDate = currentDate; // уже готовая дата из picker
    } else {
      const base = currentDate ? new Date(currentDate) : new Date();
      base.setDate(base.getDate() + days);
      newDate = base.toISOString().split('T')[0];
    }
    if (source === 'roadmap') {
      await api.patch(`/roadmap/tasks/${id}/date`, { deadline: newDate });
    } else {
      await api.patch(`/tasks/${id}/date`, { task_date: newDate });
    }
    load();
  };

  const rescheduleAllToday = async () => {
    const todayTasks = active.filter(t => isToday(t.task_date));
    if (!todayTasks.length) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newDate = tomorrow.toISOString().split('T')[0];
    await Promise.all(todayTasks.map(t => {
      if (t.source === 'roadmap') return api.patch(`/roadmap/tasks/${t.id}/date`, { deadline: newDate });
      return api.patch(`/tasks/${t.id}/date`, { task_date: newDate });
    }));
    load();
  };

  const rescheduleAllOverdue = async () => {
    const overdueTasks = active.filter(t => isOverdue(t.task_date));
    if (!overdueTasks.length) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newDate = tomorrow.toISOString().split('T')[0];
    await Promise.all(overdueTasks.map(t => {
      if (t.source === 'roadmap') return api.patch(`/roadmap/tasks/${t.id}/date`, { deadline: newDate });
      return api.patch(`/tasks/${t.id}/date`, { task_date: newDate });
    }));
    load();
  };

  const saveOrder = async (keys) => {
    const order = new Map(keys.map((k, i) => [k, i]));
    const items = keys.map(k => { const m = k.match(/^([a-z]+)(\d+)$/); return { source: m[1], id: +m[2] }; });
    // сразу показываем новый порядок, не дожидаясь сервера
    setAllTasks(prev => Object.fromEntries(Object.entries(prev).map(([src, list]) =>
      [src, list.map(t => order.has(src + t.id) ? { ...t, sort_order: order.get(src + t.id) } : t)])));
    try { await api.post('/tasks/reorder', { items }); }
    catch (e) { alert('Не удалось сохранить порядок'); load(); }
  };

  const startDrag = (dragKey, group) => {
    const g = filteredGroups.find(x => x.key === group);
    if (g) setDrag({ group, dragKey, keys: g.tasks.map(t => t.source + t.id), start: g.tasks.map(t => t.source + t.id).join() });
  };

  // Тянуть можно за всю карточку: мышью — сдвинул на 6 px и тащишь; пальцем — подержал 0,3 с (иначе это прокрутка)
  const cardPointerDown = (e, key, group) => {
    // текст задачи и комментарии (data-nodrag) — для выделения и копирования, за них не тянем
    if (e.button > 0 || e.target.closest('input,textarea,button,a,select,label,[data-nodrag]')) return;
    const touch = e.pointerType !== 'mouse';
    const x0 = e.clientX, y0 = e.clientY;
    let timer = null;
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', cleanup);
      window.removeEventListener('pointercancel', cleanup);
    };
    const go = () => { cleanup(); if (touch && navigator.vibrate) navigator.vibrate(25); startDrag(key, group); };
    const move = (ev) => {
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < 6) return;
      if (touch) cleanup(); else go();
    };
    if (touch) timer = setTimeout(go, 300);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
  };

  useEffect(() => {
    if (!drag?.dragKey) return;
    const move = (e) => {
      const cur = dragRef.current; if (!cur) return;
      if (e.clientY < 60) window.scrollBy(0, -12);
      else if (e.clientY > window.innerHeight - 60) window.scrollBy(0, 12);
      const el = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-task-key]');
      if (!el || el.dataset.group !== cur.group) return;
      const target = el.dataset.taskKey;
      if (target === cur.dragKey) return;
      const keys = cur.keys.filter(k => k !== cur.dragKey);
      keys.splice(cur.keys.indexOf(target), 0, cur.dragKey);
      setDrag({ ...cur, keys });
    };
    const up = () => {
      const cur = dragRef.current;
      setDrag(null);
      document.body.style.userSelect = '';
      // клик после перетаскивания не должен открывать карточку клиента
      const eat = ev => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener('click', eat, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', eat, { capture: true }), 300);
      if (cur && cur.keys.join() !== cur.start) saveOrder(cur.keys);
    };
    const noScroll = ev => ev.preventDefault();            // палец тащит задачу, а не страницу
    document.body.style.userSelect = 'none';
    window.getSelection()?.removeAllRanges();
    window.addEventListener('touchmove', noScroll, { passive: false });
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('touchmove', noScroll);
    };
  }, [drag?.dragKey]);

  // Объединяем все задачи с меткой source
  const merged = [
    ...allTasks.client.map(t => ({ ...t, source: 'client' })),
    ...allTasks.personal.map(t => ({ ...t, source: 'personal' })),
    ...allTasks.roadmap.map(t => ({ ...t, source: 'roadmap' })),
    ...allTasks.case.map(t => ({ ...t, source: 'case' })),
  ];

  const isWork = t => t.source === 'client' || t.source === 'case';
  const isClaude = t => /claude/i.test(t.text || '');   // задачи, которые ставит и выполняет Claude: статьи, отчёты, продвижение
  const bySource = sourceTab === 'all' ? merged
    : sourceTab === 'work' ? merged.filter(isWork)
    : sourceTab === 'claude' ? merged.filter(isClaude)
    : merged.filter(t => t.source === sourceTab);

  const searchLower = search.toLowerCase();
  const filtered = search
    ? bySource.filter(t => t.client_name?.toLowerCase().includes(searchLower) || t.text?.toLowerCase().includes(searchLower))
    : bySource;

  const active = filtered.filter(t => !t.done);
  const done   = filtered.filter(t => t.done);

  const todayCount   = active.filter(t => isToday(t.task_date)).length;
  const overdueCount = active.filter(t => isOverdue(t.task_date)).length;

  const groups = groupTasks(filtered);
  const filteredGroups = (filter === 'today'
    ? [{ key:'today', label:'Сегодня', color:'var(--accent)', tasks: active.filter(t=>isToday(t.task_date)) }]
    : filter === 'overdue'
    ? [{ key:'overdue', label:'Просрочено', color:'var(--red)', tasks: active.filter(t=>isOverdue(t.task_date)) }]
    : groups
  ).map(g => {
    if (drag && drag.group === g.key) {      // во время перетаскивания — порядок из drag.keys
      const byKey = new Map(g.tasks.map(t => [t.source + t.id, t]));
      return { ...g, tasks: drag.keys.map(k => byKey.get(k)).filter(Boolean) };
    }
    return { ...g, tasks: byManualOrder(g.tasks) };
  });

  const SOURCE_TABS = [
    { key: 'all',      label: `Все (${merged.filter(t=>!t.done).length})`,           color: 'var(--text)' },
    { key: 'work',     label: `Клиенты и дела (${merged.filter(t=>!t.done && isWork(t)).length})`, color: '#4f6ef7' },
    { key: 'client',   label: `Клиентские (${allTasks.client.filter(t=>!t.done).length})`,   color: '#7c6cf0' },
    { key: 'personal', label: `Личные (${allTasks.personal.filter(t=>!t.done).length})`,     color: '#10b981' },
    { key: 'roadmap',  label: `Развитие (${allTasks.roadmap.filter(t=>!t.done).length})`,    color: '#f5a623' },
    { key: 'case',     label: `Дела (${allTasks.case.filter(t=>!t.done).length})`,           color: '#0ea5e9' },
    { key: 'claude',   label: `С Claude (${merged.filter(t=>!t.done && isClaude(t)).length})`,   color: '#64748b' },
  ];

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Задачи</h1></div>

      <div style={{marginBottom:16}}>
        <input placeholder="Поиск по клиенту или задаче..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{width:'100%',padding:'9px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-input)',color:'var(--text)',fontSize:14,boxSizing:'border-box'}} />
      </div>

      {/* Вкладки источника */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
        {SOURCE_TABS.map(s => (
          <button key={s.key} onClick={()=>setSourceTab(s.key)} style={{
            padding:'6px 16px',borderRadius:20,border:`2px solid ${sourceTab===s.key?s.color:'var(--border)'}`,
            background:sourceTab===s.key?s.color+'22':'transparent',
            color:sourceTab===s.key?s.color:'var(--text-muted)',
            fontWeight:sourceTab===s.key?700:400,cursor:'pointer',fontSize:13,transition:'all 0.15s'
          }}>{s.label}</button>
        ))}
      </div>

      {/* Счётчики */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {[
          {key:'active',  val:active.length,  label:'Всего активных', color:'var(--text)'},
          {key:'today',   val:todayCount,      label:'На сегодня',     color:'var(--accent)'},
          {key:'overdue', val:overdueCount,    label:'Просрочено',     color:overdueCount>0?'var(--red)':'var(--text)'},
        ].map(c=>(
          <div key={c.key} className="card" style={{textAlign:'center',padding:16,cursor:'pointer',borderColor:filter===c.key?c.color:undefined}}
            onClick={()=>setFilter(c.key)}>
            <div style={{fontSize:28,fontWeight:700,color:c.color}}>{c.val}</div>
            <div style={{fontSize:13,color:'var(--text-muted)'}}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Вкладки фильтра */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <div className="tabs" style={{margin:0}}>
          {[
            {key:'active',  label:`Активные (${active.length})`},
            {key:'today',   label:`Сегодня (${todayCount})`},
            {key:'overdue', label:`Просрочено (${overdueCount})`},
            {key:'done',    label:`Выполненные (${done.length})`},
          ].map(f=>(
            <button key={f.key} className={`tab ${filter===f.key?'active':''}`} onClick={()=>setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {overdueCount > 0 && (
            <button onClick={rescheduleAllOverdue} style={{
              padding:'7px 14px',borderRadius:8,border:'1px solid var(--red)',
              background:'rgba(229,57,53,0.07)',color:'var(--red)',fontWeight:600,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'
            }}>
              Перенести просроченные на завтра ({overdueCount})
            </button>
          )}
          {todayCount > 0 && (
            <button onClick={rescheduleAllToday} style={{
              padding:'7px 14px',borderRadius:8,border:'1px solid #f5a623',
              background:'#fff8ee',color:'#c47a00',fontWeight:600,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'
            }}>
              Перенести все на завтра ({todayCount})
            </button>
          )}
        </div>
      </div>

      {/* Список */}
      {filter !== 'done' && (
        filteredGroups.length && filteredGroups.some(g=>g.tasks.length) ? filteredGroups.map(group => (
          <div key={group.key} className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
            <div onClick={FOLDED.includes(group.key) ? ()=>setOpenGroups(o=>({...o,[group.key]:!o[group.key]})) : undefined}
              style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:14,color:group.color,display:'flex',justifyContent:'space-between',cursor:FOLDED.includes(group.key)?'pointer':'default'}}>
              <span>{group.label}{FOLDED.includes(group.key) ? (openGroups[group.key] ? ' ▾' : ' ▸ показать') : ''}</span>
              <span style={{fontWeight:400,fontSize:13,color:'var(--text-muted)'}}>{group.tasks.length} задач</span>
            </div>
            {(!FOLDED.includes(group.key) || openGroups[group.key] || !!search || filter !== 'active') && group.tasks.map(t => <TaskCard key={t.source+t.id} t={t} onToggle={toggleTask} onOpenDrawer={(cid,cn)=>setDrawer({clientId:cid,clientName:cn})} onReschedule={rescheduleTask} onEdit={editTask} onChecklist={load}
              group={group.key} onDragStart={group.tasks.length > 1 ? startDrag : undefined} onCardPointerDown={group.tasks.length > 1 ? cardPointerDown : undefined} dragging={drag?.dragKey === t.source+t.id} />)}
          </div>
        )) : <div className="empty-state"><p>Нет задач</p></div>
      )}

      {filter === 'done' && (
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:14,color:'var(--text-muted)'}}>
            Выполненные — {done.length}
          </div>
          {done.length ? done.map(t => (
            <div key={t.source+t.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 16px',borderBottom:'1px solid var(--border)',opacity:0.5}}>
              <input type="checkbox" checked={true} onChange={()=>toggleTask(t)} style={{width:18,height:18,cursor:'pointer',flexShrink:0}} />
              <div style={{flex:1}}>
                {t.source==='client' && <span style={{fontWeight:700,fontSize:13,color:'var(--accent)',marginRight:8}}>{t.client_name}</span>}
                {t.source==='case' && <span style={{fontWeight:700,fontSize:13,color:'#0ea5e9',marginRight:8}}>{t.client_name}</span>}
                <span style={{fontSize:11,padding:'1px 7px',borderRadius:10,background:SOURCE_META[t.source]?.bg,color:SOURCE_META[t.source]?.color,fontWeight:600,marginRight:8}}>{SOURCE_META[t.source]?.label}</span>
                <span style={{textDecoration:'line-through',fontSize:13}}>{t.text}</span>
              </div>
              {t.task_date && <span style={{fontSize:12,color:'var(--text-muted)',flexShrink:0}}>{formatDate(t.task_date)}</span>}
            </div>
          )) : <div className="empty-state"><p>Нет выполненных задач</p></div>}
        </div>
      )}

      {drawer && (
        <ClientDrawer clientId={drawer.clientId} clientName={drawer.clientName} onClose={()=>setDrawer(null)} onTasksChanged={load} />
      )}
    </div>
  );
}
